/* -------------------------------------------------------------------------
 * dateRangeUtils.js — pure helpers for the FilterToolbar's "dateRange"
 * filter type. Import `resolveDateRange` wherever you need the actual
 * start/end Date objects for a { preset, start, end } filter value.
 * ---------------------------------------------------------------------- */

export const DATE_RANGE_PRESETS = [
  { value: "today", label: "Today" },
  { value: "this_week", label: "This Week" },
  { value: "this_month", label: "This Month" },
  { value: "this_year", label: "This Year" },
  { value: "custom", label: "Custom" },
];

const startOfDay = (d) => {
  const n = new Date(d);
  n.setHours(0, 0, 0, 0);
  return n;
};

const endOfDay = (d) => {
  const n = new Date(d);
  n.setHours(23, 59, 59, 999);
  return n;
};

const startOfWeek = (d) => {
  const n = startOfDay(d);
  const day = n.getDay(); // 0 = Sunday
  const diff = (day === 0 ? 6 : day - 1); // week starts Monday
  n.setDate(n.getDate() - diff);
  return n;
};

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const startOfYear = (d) => new Date(d.getFullYear(), 0, 1);

/**
 * resolveDateRange({ preset, start, end }) -> { start: Date|null, end: Date|null }
 * `start`/`end` are only read when preset === "custom" (expects "YYYY-MM-DD" strings).
 */
export function resolveDateRange(rangeValue) {
  const { preset, start, end } = rangeValue || {};
  const now = new Date();

  switch (preset) {
    case "today":
      return { start: startOfDay(now), end: endOfDay(now) };
    case "this_week":
      return { start: startOfWeek(now), end: endOfDay(now) };
    case "this_month":
      return { start: startOfMonth(now), end: endOfDay(now) };
    case "this_year":
      return { start: startOfYear(now), end: endOfDay(now) };
    case "custom":
      return {
        start: start ? startOfDay(new Date(start)) : null,
        end: end ? endOfDay(new Date(end)) : null,
      };
    default:
      return { start: null, end: null };
  }
}

/** Human-readable summary used for the active-filter chip, e.g. "This Week" or "Jul 13 – Jul 19". */
export function formatDateRangeSummary(rangeValue) {
  const { preset, start, end } = rangeValue || {};
  const preset_ = DATE_RANGE_PRESETS.find((p) => p.value === preset);
  if (preset === "custom" && (start || end)) {
    const fmt = (s) =>
      s ? new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "…";
    return `${fmt(start)} – ${fmt(end)}`;
  }
  return preset_ ? preset_.label : "All time";
}
